import type { CompanyUsersResponseDto } from "@/spz-api/model/companyUsersResponseDto";
import { UserSearchableSelect } from "@spz/ui/components/user-searchable-select";
import { cn } from "@spz/ui/lib/utils";
import { t } from "i18next";
import type { FC } from "react";
import { useOwnersList } from "../hooks/use-owners-list";

type OwnershipSelectorProps = {
  companyId: string;
  onChange: (
    userId: string | null,
    user?: CompanyUsersResponseDto | undefined,
  ) => void;
  value: string | null;
  initialValue?: CompanyUsersResponseDto | null;
  disabled?: boolean;
  placeholder?: string;
  readonly?: boolean;
  isBulkSelection?: boolean;
};

export const OwnershipSelector: FC<OwnershipSelectorProps> = ({
  companyId,
  onChange,
  value,
  initialValue,
  disabled,
  placeholder,
  readonly = false,
  isBulkSelection = false,
}) => {
  // Only fetch users if not readonly
  const { owners, isLoading, error } = useOwnersList(companyId);

  const sortedOwners = [...(owners ?? [])].sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.trim().toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const enhancedUsers: CompanyUsersResponseDto[] = [
    {
      id: "unassigned",
      firstName: "",
      lastName: "",
      email: "",
      avatarUrl: null,
    },
    ...sortedOwners,
  ];

  const effectivePlaceholder = isBulkSelection
    ? t("bulk_change_owner_dropdown_placeholder")
    : placeholder;

  const effectiveDefaultValue = isBulkSelection
    ? undefined
    : (initialValue ?? undefined);

  const effectiveValue = isBulkSelection
    ? undefined
    : value
      ? [value]
      : ["unassigned"];

  return (
    <UserSearchableSelect
      onSelect={(ids) => {
        const selectedId = ids[0];
        const selectedUser = enhancedUsers.find((u) => u.id === selectedId);
        onChange(selectedId === "unassigned" ? null : selectedId, selectedUser);
      }}
      defaultValue={effectiveDefaultValue}
      value={effectiveValue}
      disabled={disabled}
      placeholder={effectivePlaceholder}
      users={enhancedUsers}
      error={error}
      isLoading={isLoading}
      className={cn(
        "min-w-[200px] max-w-md",
        readonly && "border-none !cursor-not-allowed",
      )}
      size="small"
      emptyLabel={t("unassigned")}
      readonly={readonly}
    />
  );
};
